import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from '@g4/moment-utc';
import { withValue } from '@g4/automation-hooks';
import { getPrice } from '@g4/next-furniture/Price/Price';

import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import { Box, Text, useMedia } from '../../../Boneyard';
import CALENDAR_ICON from '../../../../images/ic-calendar.svg';
import BED_ICON from '../../../../images/ic-bed.svg';
import AVATAR_ICON from '../../../../images/ic-avatar.svg';
import INFO_ICON from '../../../../images/icon-24-info-blue.svg';
import Markdown from '../../../Markdown';

const propTypes = {
  automationHook: PropTypes.string,
  hotelItem: PropTypes.shape({
    id: PropTypes.string,
    roomType: PropTypes.string,
    roomsCount: PropTypes.number,
    adultCount: PropTypes.number,
    checkin: PropTypes.shape({
      time: PropTypes.string,
    }),
    checkout: PropTypes.shape({
      time: PropTypes.string,
    }),
    hotel: PropTypes.shape({
      name: PropTypes.string,
    }),
    childrenCount: PropTypes.number,
    hotelPrice: PropTypes.shape({
      totalMandatoryFees: PropTypes.number,
    }),
  }),
  isTripSummary: PropTypes.bool,
  hotelCheckInLabel: PropTypes.string,
  hotelCheckOutLabel: PropTypes.string,
};

const BorderBox = styled(Box)`
  border-bottom: ${props =>
    props.isTripSummary && !props.isMobile
      ? `1px solid ${props.theme.colors.cart.footerBorder}`
      : 'none'};
`;

const CalendarIconContainer = styled.div`
  width: ${props => (props.isMobile ? '16px' : '24px')};
  height: ${props => (props.isMobile ? '16px' : '24px')};
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: ${props => (props.isMobile ? '10px' : '4px')};
  margin-left: ${props => (props.isMobile ? '4px' : '0px')};
  background-image: url(${CALENDAR_ICON});
`;

const BedIconContainer = styled.div`
  width: ${props => (props.isMobile ? '16px' : '24px')};
  height: ${props => (props.isMobile ? '16px' : '24px')};
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: ${props => (props.isMobile ? '10px' : '4px')};
  background-image: url(${BED_ICON});
  flex-shrink: 0;
`;

const AvatarIconContainer = styled.div`
  width: ${props => (props.isMobile ? '16px' : '24px')};
  height: ${props => (props.isMobile ? '16px' : '24px')};
  background-size: contain;
  background-repeat: no-repeat;
  margin-top: ${props => (props.isMobile ? '6px' : '4px')};
  background-image: url(${AVATAR_ICON});
  flex-shrink: 0;
`;

const InfoBlueIcon = styled.img`
  margin-right: 5px;
  margin-top: ${props => (props.isMobile ? '0px' : '1px')};
`;

const MandatoryFeesInfoMessage = styled(Text)`
  margin-left: 5px;
`;

const HotelDateTime = ({ automationHook, time, isTripSummary }) => {
  const mq = useMedia();
  const timeMoment = moment(time);
  const dateFormat = 'MMM DD, YYYY';

  return (
    <Text
      automationHook={automationHook}
      size={isTripSummary ? (mq.lt('md') ? 13 : 16) : mq.gte('md') ? 14 : 13}
    >
      <time dateTime={timeMoment.toISOString()}>
        {timeMoment.format(dateFormat)}
      </time>
    </Text>
  );
};

HotelDateTime.propTypes = {
  automationHook: PropTypes.string,
  time: PropTypes.string,
  isTripSummary: PropTypes.bool,
};

const HotelInfo = ({ automationHook, hotelItem, isTripSummary }) => {
  const mq = useMedia();
  const isMobile = mq.lt('md');
  const hotelInfoAutomationHook = withValue(automationHook, 'hotel-info');

  const translations = useTranslations();

  const {
    hotelCheckInLabel,
    hotelCheckOutLabel,
    resortFeesTotalLabelHTML,
  } = isTripSummary ? translations.paymentPage : translations.cart;

  const {
    bookedHotelRoomsCountText,
    bookedHotelRoomsCountTextPlural,
    bookedHotelRoomAdultCountText,
    bookedHotelRoomAdultCountTextPlural,
    bookedHotelRoomChildrenCountText,
    bookedHotelRoomChildrenCountTextPlural,
  } = translations.confirmationPage;

  const roomsCount = hotelItem?.roomsCount;
  const adultCount = hotelItem?.adultCount;
  const childrenCount = hotelItem?.childrenCount;
  const totalMandatoryFees = hotelItem?.hotelPrice?.totalMandatoryFees ?? 0;

  const roomsCountTranslation =
    roomsCount === 0
      ? ''
      : roomsCount > 1
      ? bookedHotelRoomsCountTextPlural
      : bookedHotelRoomsCountText;
  const adultCountTranslation =
    adultCount === 0
      ? ''
      : adultCount > 1
      ? bookedHotelRoomAdultCountTextPlural
      : bookedHotelRoomAdultCountText;
  const childrenCountTranslation =
    childrenCount === 0
      ? ''
      : childrenCount > 1
      ? bookedHotelRoomChildrenCountTextPlural
      : bookedHotelRoomChildrenCountText;

  return (
    <Box key={hotelItem.id} pl={isTripSummary ? 1 : 3}>
      <BorderBox
        mb={isTripSummary && isMobile ? 0 : 2}
        pb={isTripSummary ? (isMobile ? 0 : 2) : 0}
        isTripSummary={isTripSummary}
        isMobile={isMobile}
      >
        <Text
          weight={600}
          size={isTripSummary ? (isMobile ? 14 : 18) : isMobile ? 14 : 16}
        >
          {hotelItem?.hotel?.name}
        </Text>
        {isTripSummary ? (
          ''
        ) : (
          <Box mt={1}>
            <Text weight={500} size={isMobile ? 13 : 14}>
              {hotelItem.roomType}
            </Text>
          </Box>
        )}
        <Text
          automationHook={withValue(hotelInfoAutomationHook, 'room-count')}
          weight={500}
          size={isMobile ? 13 : 14}
        >
          <Markdown
            source={`${roomsCountTranslation}, ${adultCountTranslation}${
              childrenCount > 0 ? ', ' : ''
            }${childrenCountTranslation}`}
            data={{ roomsCount, adultCount, childrenCount }}
          />
        </Text>
      </BorderBox>

      <Box
        d="flex"
        direction={isTripSummary ? 'row' : 'column'}
        ml={isTripSummary ? (isMobile ? 0 : 3) : 0}
      >
        <Box
          d="flex"
          direction={isTripSummary ? (isMobile ? 'column' : 'row') : 'column'}
        >
          <Box
            d="flex"
            alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
            mb={isTripSummary && isMobile ? 0 : 1}
          >
            {isTripSummary ? <CalendarIconContainer isMobile={isMobile} /> : ''}
            <Box
              d="flex"
              direction={isTripSummary ? 'column' : 'row'}
              alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
              ml={isTripSummary ? (isMobile ? 2 : 4) : 0}
              mt={isTripSummary ? 2 : 0}
              mr={isTripSummary ? (isMobile ? 4 : 9) : 0}
            >
              <Text
                automationHook={withValue(
                  hotelInfoAutomationHook,
                  'check-in-label'
                )}
                weight={500}
                size={isTripSummary ? (isMobile ? 12 : 16) : 12}
              >
                {hotelCheckInLabel}:
              </Text>
              <Box
                mt={isTripSummary ? (isMobile ? 1 : 2) : 0}
                ml={isTripSummary ? 0 : 6}
              >
                <HotelDateTime
                  automationHook={withValue(
                    hotelInfoAutomationHook,
                    'check-in-time'
                  )}
                  time={hotelItem.checkin.time}
                  isTripSummary={isTripSummary}
                />
              </Box>
            </Box>
          </Box>

          <Box d="flex" alignItems={isTripSummary ? 'flex-start' : 'flex-end'}>
            {isTripSummary ? <CalendarIconContainer isMobile={isMobile} /> : ''}
            <Box
              d="flex"
              alignItems={isTripSummary ? 'flex-start' : 'flex-end'}
              direction={isTripSummary ? 'column' : 'row'}
              ml={isTripSummary ? (isMobile ? 2 : 4) : 0}
              mt={isTripSummary ? 2 : 0}
              mr={isTripSummary ? (isMobile ? 4 : 12) : 0}
            >
              <Text
                automationHook={withValue(
                  hotelInfoAutomationHook,
                  'check-out-label'
                )}
                weight={500}
                size={isTripSummary ? (isMobile ? 12 : 16) : 12}
              >
                {hotelCheckOutLabel}:
              </Text>
              <Box
                mt={isTripSummary ? (isMobile ? 1 : 2) : 0}
                ml={isTripSummary ? 0 : 4}
              >
                <HotelDateTime
                  automationHook={withValue(
                    hotelInfoAutomationHook,
                    'check-out-time'
                  )}
                  time={hotelItem.checkout.time}
                  isTripSummary={isTripSummary}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {isTripSummary ? (
          <Box d="flex" direction={isMobile ? 'column' : 'row'} w="50%">
            <Box d="flex" mr={isMobile ? 0 : 10} mb={isMobile ? 0 : 0}>
              <BedIconContainer isMobile={isMobile} />
              <Box
                d="flex"
                ml={isMobile ? 2 : 4}
                mt={2}
                h={43}
                maxWidth={isMobile ? 130 : 247}
              >
                <Text
                  automationHook={withValue(
                    hotelInfoAutomationHook,
                    'room-type'
                  )}
                  weight={500}
                  size={isMobile ? 13 : 15}
                >
                  {hotelItem.roomType}
                </Text>
              </Box>
            </Box>
            <Box d="flex">
              <AvatarIconContainer isMobile={isMobile} />
              <Box d="flex" ml={isMobile ? 2 : 4} mt={isMobile ? 1 : 2}>
                <Text
                  automationHook={withValue(
                    hotelInfoAutomationHook,
                    'room-count'
                  )}
                  weight={500}
                  size={isMobile ? 13 : 15}
                >
                  <Markdown
                    source={`${roomsCountTranslation}, ${adultCountTranslation}${
                      childrenCount > 0 ? ', ' : ''
                    }${childrenCountTranslation}`}
                    data={{ roomsCount, adultCount, childrenCount }}
                  />
                </Text>
              </Box>
            </Box>
          </Box>
        ) : (
          ''
        )}
      </Box>

      {isTripSummary && totalMandatoryFees > 0 && (
        <Box ml={isMobile ? 0 : 3} mt={2} mb={2}>
          <Box d="flex" alignItems="flex-start">
            <InfoBlueIcon src={INFO_ICON} alt="" isMobile={isMobile} />
            <MandatoryFeesInfoMessage
              size={isMobile ? 12 : 15}
              isMobile={isMobile}
              automationHook={withValue(
                hotelInfoAutomationHook,
                'resort-fees-total-label'
              )}
            >
              <Markdown
                source={resortFeesTotalLabelHTML}
                data={{
                  totalfees: getPrice(totalMandatoryFees).price,
                }}
              />
            </MandatoryFeesInfoMessage>
          </Box>
        </Box>
      )}
    </Box>
  );
};

HotelInfo.propTypes = propTypes;

export default HotelInfo;
